/**
 * The /graha/ hub check's form props, frame, slot floors and ids, shared by the
 * server-rendered shell (GrahaChartCheckLazy), the island's BirthDetailsForm
 * and BirthToolFormShell.parity.test.tsx, so every frame before and after the
 * island loads reads the same words. No name field: the answer is the reader's
 * own chart. The birth time is optional: the date alone settles the signs of
 * the grahas that hold one sign all day; the houses, the Lagna lord and the
 * running dasha say they need the time.
 *
 * The /hi/graha/ hub's twin of the check (GRAHA_CHART_CHECK_HI_FORM and
 * GRAHA_CHART_CHECK_HI_COPY below) is the card's governed Hinglish (rule 21,
 * c-forms-10), the register of BirthDetailsForm.i18n.ts:
 * scripts/check_hindi_register.py keeps Devanagari out of this whole file,
 * comments included. The Hindi section's heading and lead and the Hindi
 * answer's text are page copy and stay Devanagari in their components
 * (GrahaChartCheckSection.tsx, GrahaChartAnswerHi.tsx).
 */
export const GRAHA_CHART_CHECK_FORM = {
  language: "en",
  submitLabel: "See My Nine Grahas",
  collectName: false,
  requireBirthTime: false,
} as const;

/**
 * One box for every state of the check's slot (the career check's frame): the
 * shell, the loading shell, the live form and the answer each fill the slot's
 * reserved height, and the submit sits at the foot of the box in every state
 * with one 48 px height.
 */
export const GRAHA_CHART_CHECK_FRAME =
  "mx-auto flex w-full max-w-md flex-1 flex-col [&>form]:flex [&>form]:flex-1 [&>form]:flex-col [&>form>:last-child]:mt-auto [&>form>:last-child]:min-h-12";

/** The answer is a nine-row table, so it takes the section's full width, not the form's. */
export const GRAHA_CHART_ANSWER_FRAME = "flex w-full flex-1 flex-col";

/**
 * The form's room: the slot's floor (GrahaChartCheckLazy has the measured
 * heights) and, once the answer's room is added, the form frame's own height,
 * so the submit stays where the press found it.
 */
export const GRAHA_CHART_FORM_RESERVE = "min-h-[600px] sm:min-h-[540px]";

/**
 * The answer's room, added by the island inside the submit itself, before the
 * request goes out (the career and love checks' pattern): the growth happens in
 * the press, an input-driven change rather than a layout shift, and the reply,
 * an error or a retry all land inside it; only "Check another birth date" or
 * "Add your birth time" releases it. Measured 2026-10-20 on next dev with
 * POST /kundali mocked (Playwright, service workers blocked, 7 widths from 320
 * to 1280) with the tallest answer tested (four distinct grahas in
 * "matters most", long sign and nakshatra names, three flags on most rows,
 * each row's flags under its graha's name so all four columns fit a phone):
 * 1956 px at 320 wide, 1859 at 360, 1817 at 390, 1736 at 414, 1567 at 480, 1441
 * at 640 and 1216 at 1280. The date-only answer with every sign settled is 1319
 * at 320 and 902 at 640. Hence 1980 px on phones and 1460 from the sm
 * breakpoint; the answer card stretches to fill it, its button at the foot. A
 * static reserve this tall would leave a long blank under the form for every
 * reader who never checks a chart.
 */
export const GRAHA_CHART_ANSWER_RESERVE = "min-h-[1980px] sm:min-h-[1460px]";

/** The real form's input ids (no heading prop, so BirthDetailsForm's "-default" ids). */
export const GRAHA_CHART_CHECK_FIELD_IDS = {
  date: "birthDate-default",
  time: "birthTime-default",
  place: "birthPlace-default",
} as const;

/** free_tool_started and calculator_used placement for the /graha/ hub check. */
export const GRAHA_CHART_CHECK_PLACEMENT = "graha_hub_check";

/** free_tool_started and calculator_used placement for the /hi/graha/ hub check. */
export const GRAHA_CHART_CHECK_HI_PLACEMENT = "hi_graha_hub_check";

/**
 * The Hindi check's controls: the submit, the failure lines and the answer's
 * two buttons. "Navgrah dekhein" stays on one line at 320 px inside the Hindi
 * hub's 20 px gutter (the 2026-10-14 graha "jaanein" lesson: a label that
 * wraps grows back when the one-line loading label replaces it).
 */
export const GRAHA_CHART_CHECK_HI_COPY = {
  submit: "Navgrah dekhein",
  unavailable: "Calculation nahi ho payi — kripya dobara try karein.",
  unreadable: "Yeh result padha nahi ja saka. Kripya dobara try karein.",
  checkAnother: "Doosri janm tithi check karein",
  addTime: "Janm samay daalein",
} as const;

/**
 * The Hindi form props, shared by the shell, the island's BirthDetailsForm and
 * BirthToolFormShell.parity.test.tsx, as GRAHA_CHART_CHECK_FORM is: no name
 * field and an optional birth time, with the card's default "Janm samay nahi
 * pata?" explainer (the English check keeps its default too).
 */
export const GRAHA_CHART_CHECK_HI_FORM = {
  language: "hi",
  submitLabel: GRAHA_CHART_CHECK_HI_COPY.submit,
  collectName: false,
  requireBirthTime: false,
} as const;
